// R/L 共通の諸元（dimensions）手入力バリデーション。BEV/HEV とも phenomenon_id をキーに
// gear_ratio / motor_order を入力する UI（issue #26）で使う。
//
// 受け入れ条件（issue #26 追加指摘 2026-07-13）:
// - tire_radius は 0 より大きいこと
// - 各 phenomenon は gear_ratio/motor_order の両方が入力済み(>0)か、両方とも空欄のいずれかのみ許可
//   （片側だけ入力されている状態＝片側欠損は不可）
// - 少なくとも 1 つの phenomenon が入力済みであること
// - 送信 body には入力済みの phenomenon だけを含める

const toPositiveNumber = (value: string): number | null => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export type PhenomenonEntryState = "filled" | "empty" | "partial";

// gearRatioInput/motorOrderInput の状態判定と数値変換を 1 回のパースで行う。
// classifyPhenomenonEntry と buildDimensionsParams の両方から使う内部ヘルパー。
const parsePhenomenonEntry = (
    gearRatioInput: string,
    motorOrderInput: string,
): { state: PhenomenonEntryState; gear: number | null; motor: number | null } => {
    const gearEmpty = gearRatioInput.trim() === "";
    const motorEmpty = motorOrderInput.trim() === "";
    if (gearEmpty && motorEmpty) return { state: "empty", gear: null, motor: null };
    if (gearEmpty || motorEmpty) return { state: "partial", gear: null, motor: null };
    const gear = toPositiveNumber(gearRatioInput);
    const motor = toPositiveNumber(motorOrderInput);
    return { state: gear !== null && motor !== null ? "filled" : "partial", gear, motor };
};

// 1 phenomenon の gear_ratio / motor_order 入力状態を判定する。
export const classifyPhenomenonEntry = (
    gearRatioInput: string,
    motorOrderInput: string,
): PhenomenonEntryState => parsePhenomenonEntry(gearRatioInput, motorOrderInput).state;

// 入力文字列群から送信用 params を構築する。片側欠損が 1 件でもある場合、
// tire_radius が不正な場合、入力済み phenomenon が 1 件もない場合は null を返す。
export const buildDimensionsParams = (
    tireRadiusInput: string,
    gearRatioInputs: Record<string, string>,
    motorOrderInputs: Record<string, string>,
    phenomenonIds: readonly string[],
): { tire_radius: number; gear_ratio: Record<string, number>; motor_order: Record<string, number> } | null => {
    const tireRadius = toPositiveNumber(tireRadiusInput);
    if (tireRadius === null) return null;

    const gear_ratio: Record<string, number> = {};
    const motor_order: Record<string, number> = {};

    for (const p of phenomenonIds) {
        const { state, gear, motor } = parsePhenomenonEntry(gearRatioInputs[p] ?? "", motorOrderInputs[p] ?? "");
        if (state === "partial") return null;
        if (state === "filled") {
            gear_ratio[p] = gear!;
            motor_order[p] = motor!;
        }
    }

    if (Object.keys(gear_ratio).length === 0) return null;

    return { tire_radius: tireRadius, gear_ratio, motor_order };
};
